import { createApi } from '@reduxjs/toolkit/dist/query/react';

import { BugReportStatus } from '../../../shared/constants';
import { IBugReport } from '../../../types/interfaces/bug-report';
import { IBugReportMessage } from '../../../types/interfaces/bug-report-message';
import { IBugReportType } from '../../../types/interfaces/bug-report-type';
import { INewFile } from '../../../types/interfaces/new-file';
import { BUG_REPORTS, HTTP_METHOD, REDUX_CACHE } from '../constants';
import * as endpoints from '../endpoints';
import {
  TransformedErrorResponseWithNotification,
  TransformedResponseWithNotification,
  getBaseQuery,
  getListProvidesTags,
} from '../generic';

export const bugReportApi = createApi({
  reducerPath: 'bugReportApi',
  tagTypes: [
    BUG_REPORTS.BUG_REPORT,
    BUG_REPORTS.BUG_REPORT_HISTORY,
    BUG_REPORTS.BUG_REPORTS,
    BUG_REPORTS.BUG_REPORT_TYPES,
    REDUX_CACHE.ABORT_INVALIDATION,
    REDUX_CACHE.LIST,
  ],
  baseQuery: getBaseQuery(endpoints.BugReports.ROOT),
  // global configuration for the api
  keepUnusedDataFor: 60,
  endpoints: (builder) => ({
    getBugReports: builder.query<IBugReport[], void>({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: '',
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, BUG_REPORTS.BUG_REPORTS),
      ...TransformedErrorResponseWithNotification(),
    }),
    getBugReportArchived: builder.query<IBugReport[], void>({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.BugReports.ARCHIVED,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, BUG_REPORTS.BUG_REPORT_HISTORY),
      ...TransformedErrorResponseWithNotification(),
    }),
    getBugReportTypes: builder.query<IBugReportType[], void>({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.BugReports.TYPES,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, BUG_REPORTS.BUG_REPORT_TYPES),
      ...TransformedErrorResponseWithNotification(),
    }),
    getBugReportByIdApi: builder.query<IBugReport, string>({
      keepUnusedDataFor: 3600,
      query(id: string) {
        return {
          url: endpoints.BugReports.REPORT_ID(id),
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) => [{ type: BUG_REPORTS.BUG_REPORT }],
      ...TransformedErrorResponseWithNotification(),
    }),
    postPutBugReportTypeApi: builder.mutation<
      void,
      {
        id: string;
        body: IBugReportType;
      }
    >({
      query({ id, body }) {
        return {
          url: `${endpoints.BugReports.TYPES}${id ? '/' + id : ''}`,
          method: id ? HTTP_METHOD.PUT : HTTP_METHOD.POST,
          body,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: BUG_REPORTS.BUG_REPORT_TYPES,
              id: REDUX_CACHE.LIST,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification(),
    }),
    deleteBugReportTypeApi: builder.mutation<void, string>({
      query(id) {
        return {
          url: `${endpoints.BugReports.TYPES}/${id}`,
          method: HTTP_METHOD.DELETE,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: BUG_REPORTS.BUG_REPORT_TYPES,
              id: REDUX_CACHE.LIST,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification(),
    }),
    postBugReportMessageApi: builder.mutation<
      void,
      {
        reportId: string;
        body: {
          message: IBugReportMessage;
          status: BugReportStatus;
          filesToUpload: INewFile[];
        };
      }
    >({
      query({ reportId, body }) {
        return {
          url: endpoints.BugReports.MESSAGE(reportId),
          method: HTTP_METHOD.POST,
          body,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: BUG_REPORTS.BUG_REPORT,
            },
            {
              type: BUG_REPORTS.BUG_REPORTS,
              id: REDUX_CACHE.LIST,
            },
            {
              type: BUG_REPORTS.BUG_REPORT_HISTORY,
              id: REDUX_CACHE.LIST,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification(),
    }),
    postBugReportStatusApi: builder.mutation<
      void,
      {
        reportId: string;
        body: { status: BugReportStatus };
      }
    >({
      query({ reportId, body }) {
        return {
          url: endpoints.BugReports.STATUS(reportId),
          method: HTTP_METHOD.POST,
          body,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: BUG_REPORTS.BUG_REPORTS,
              id: REDUX_CACHE.LIST,
            },
            {
              type: BUG_REPORTS.BUG_REPORT_HISTORY,
              id: REDUX_CACHE.LIST,
            },
            {
              type: BUG_REPORTS.BUG_REPORT,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification(),
    }),
  }),
});

export const {
  useGetBugReportsQuery: useGetBugReports,
  useGetBugReportArchivedQuery: useGetBugReportArchived,
  useGetBugReportTypesQuery: useGetBugReportTypes,
  useGetBugReportByIdApiQuery: useGetBugReportById,
  usePostPutBugReportTypeApiMutation: usePostPutBugReportType,
  useDeleteBugReportTypeApiMutation: useDeleteBugReportTypeApi,
  usePostBugReportMessageApiMutation: usePostBugReportMessage,
  usePostBugReportStatusApiMutation: usePostBugReportStatus,
} = bugReportApi;
